import { useEffect, useRef } from "react";
import {
  createTheme,
  ThemeProvider as MuiThemeProvider,
  StyledEngineProvider,
  PaletteMode,
  PaletteColorOptions,
} from "@mui/material";
import { pink, teal } from "@mui/material/colors";

import useBoundStore from "store";

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    lowercaseSimple: true;
  }
}

const setProviderTheme = (
  mode: PaletteMode,
  { primary, secondary }: { primary: PaletteColorOptions; secondary: PaletteColorOptions }
) => {
  const lowercaseSimpleBtnColor = mode === "light" ? "#000000" : "#ffffff";

  return createTheme({
    palette: {
      mode,
      primary,
      secondary,
    },
    components: {
      MuiLink: {
        defaultProps: {
          underline: "hover",
        },
      },
      MuiButton: {
        variants: [
          {
            props: { variant: "lowercaseSimple", size: "large" },
            style: {
              color: lowercaseSimpleBtnColor,
              border: `1px solid ${lowercaseSimpleBtnColor}`,
              padding: "7px 16px",
              fontSize: "0.9375rem",
            },
          },
          {
            props: { variant: "lowercaseSimple", size: "small" },
            style: {
              color: lowercaseSimpleBtnColor,
              border: `1px solid ${lowercaseSimpleBtnColor}`,
              padding: "3px 9px",
              fontSize: "0.8125rem",
            },
          },
        ],
      },
    },
  });
};

function ThemeProvider(props: { children: React.ReactNode }) {
  const { children } = props;
  const styleSheetRef = useRef<HTMLStyleElement>();
  const isMobileDevice = useBoundStore((state) => state.miscellaneous.isMobileDevice);
  const deviceClassName = isMobileDevice ? "mobile-device" : "desktop-device";
  const primaryColor = useBoundStore((state) => state.generalSettings.primaryColor);
  const storeTheme = useBoundStore((state) => state.generalSettings.theme);
  const primary = primaryColor === "teal" ? teal : pink;
  const secondary = primary === teal ? pink : teal;
  const theme = setProviderTheme(storeTheme, {
    primary,
    secondary,
  });

  useEffect(() => {
    styleSheetRef.current = document.createElement("style");
    document.head.prepend(styleSheetRef.current);
  }, []);

  useEffect(() => {
    document.body.className = `theme-${theme.palette.mode} ${deviceClassName}`;
    if (styleSheetRef.current) {
      styleSheetRef.current.innerHTML = `
      :root {
        --theme-palette-primary-main: ${theme.palette.primary.main};
        --theme-palette-primary-contrastText: ${theme.palette.primary.contrastText};
        --theme-palette-primary-dark: ${theme.palette.primary.dark};
        --theme-palette-secondary-main: ${theme.palette.secondary.main};
        --theme-palette-success-main: ${theme.palette.success.main};
        --theme-palette-warning-main: ${theme.palette.warning.main};
        --theme-palette-info-main: ${theme.palette.info.main};
        --theme-palette-error-main: ${theme.palette.error.main};
        --theme-palette-background-paper: ${theme.palette.background.paper};
        --theme-palette-text-primary: ${theme.palette.text.primary};
        --theme-palette-text-secondary: ${theme.palette.text.secondary};
        --theme-palette-action-hover: ${theme.palette.action.hover};
        --theme-palette-action-selected: ${theme.palette.action.selected};
        --theme-palette-action-focus: ${theme.palette.action.focus};
        --theme-palette-action-disabled: ${theme.palette.action.disabled};
        --theme-palette-divider: ${theme.palette.divider};
        --theme-zIndex-fab: ${theme.zIndex.fab};
        --emoji-picker-bg: ${theme.palette.mode === "light" ? "255, 255, 255" : "56, 56, 56"};
      }`;
    }
  }, [theme, styleSheetRef.current]);

  return (
    <StyledEngineProvider injectFirst>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </StyledEngineProvider>
  );
}

export default ThemeProvider;
